import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiPhone, FiMail, FiGlobe, FiMapPin, FiDownload, FiMessageSquare, FiFileText, FiSmartphone } from 'react-icons/fi';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';

// Public digital business card at /card/:slug. No auth — it's a business card.
export default function DigitalCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/public/card/${slug}`);
        setCard(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'This card is not available.' : 'Could not load this card.');
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>;
  }
  if (error || !card) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>{error || 'Not found'}</div>;
  }

  const co = card.company || {};
  const telHref = `tel:${(card.phone || '').replace(/[^\d+]/g, '')}`;
  const smsHref = `sms:${(card.phone || '').replace(/[^\d+]/g, '')}`;
  const vcfHref = `${API_BASE_URL}${card.vcf_url}`;

  const btn = (bg, color) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '13px 16px', borderRadius: 12, fontSize: 15, fontWeight: 600,
    textDecoration: 'none', background: bg, color, border: 'none', cursor: 'pointer', width: '100%',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '24px 16px' }}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>

        {/* Card */}
        <div style={{ background: 'white', borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
          {/* Header band */}
          <div style={{ background: '#14532d', padding: '28px 20px', textAlign: 'center', color: 'white' }}>
            {card.photo && (
              <img
                src={`${API_BASE_URL}${card.photo}`}
                alt={card.name}
                onError={(e) => { e.target.style.display = 'none'; }}
                style={{
                  width: 96, height: 96, borderRadius: card.kind === 'company' ? 12 : '50%',
                  objectFit: card.kind === 'company' ? 'contain' : 'cover',
                  background: 'white', border: '3px solid rgba(255,255,255,0.6)', marginBottom: 12,
                }}
              />
            )}
            <div style={{ fontSize: 22, fontWeight: 700 }}>{card.name}</div>
            {card.title && <div style={{ fontSize: 14, opacity: 0.85, marginTop: 3 }}>{card.title}</div>}
            {card.kind === 'person' && co.name && (
              <div style={{ fontSize: 13, opacity: 0.75, marginTop: 6 }}>{co.name}</div>
            )}
          </div>

          {/* Actions */}
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <a href={vcfHref} style={btn('#14532d', 'white')}>
              <FiDownload size={17} /> Save to Contacts
            </a>
            {card.phone && (
              <div style={{ display: 'flex', gap: 10 }}>
                <a href={telHref} style={{ ...btn('#16a34a', 'white') }}>
                  <FiPhone size={16} /> Call
                </a>
                <a href={smsHref} style={{ ...btn('#2563eb', 'white') }}>
                  <FiMessageSquare size={16} /> Text
                </a>
              </div>
            )}
            {card.email && (
              <a href={`mailto:${card.email}`} style={btn('#f1f5f9', '#0f172a')}>
                <FiMail size={16} /> Email
              </a>
            )}
            {card.quote_url && (
              <a href={card.quote_url} target="_blank" rel="noopener noreferrer" style={btn('#b45309', 'white')}>
                <FiFileText size={16} /> Request a Free Quote
              </a>
            )}
          </div>

          {/* Details */}
          <div style={{ borderTop: '1px solid #f1f5f9', padding: 18, fontSize: 14, color: '#334155', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {card.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiPhone size={14} color="#94a3b8" /> <a href={telHref} style={{ color: '#334155' }}>{card.phone}</a>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>office</span>
              </div>
            )}
            {card.cell && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiSmartphone size={14} color="#94a3b8" />
                <a href={`tel:${card.cell.replace(/[^\d+]/g, '')}`} style={{ color: '#334155' }}>{card.cell}</a>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>cell</span>
              </div>
            )}
            {card.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiMail size={14} color="#94a3b8" /> <a href={`mailto:${card.email}`} style={{ color: '#334155' }}>{card.email}</a>
              </div>
            )}
            {co.website && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiGlobe size={14} color="#94a3b8" />
                <a href={co.website} target="_blank" rel="noopener noreferrer" style={{ color: '#334155' }}>
                  {co.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
            {(co.address || co.city_state_zip) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <FiMapPin size={14} color="#94a3b8" style={{ marginTop: 3 }} />
                <span>{co.address}{co.address && co.city_state_zip ? ', ' : ''}{co.city_state_zip}</span>
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 14 }}>
          {co.name}
        </div>
      </div>
    </div>
  );
}
